import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-comparison';

/**
 * Comparison-table section contrasting features across two or more products in a tabular layout.
 * Centered heading + subtitle above a full-width HTML `<table>` with the feature label in the first column and one column per compared product; boolean cell values render as check/X icons, strings render as plain text.
 * Only type in the `comparison` folder; uses a true semantic table (vs. card grids used elsewhere) to make per-feature deltas easy to scan side-by-side.
 *
 * @category SignNow
 * @slot heading - <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]>
 * @slot subtitle - <me-text variant="large">
 * @example
 *   <sn-comparison
 *     columns='[{"label":"Feature"},{"label":"airSlate SignNow"},{"label":"DocuSign"}]'
 *     rows='[{"feature":"Unlimited documents","values":[true,false]},{"feature":"Starting price","values":["$8/mo","$15/mo"]}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Feature Comparison</me-heading>
 *     <me-text slot="subtitle" variant="large">See how we compare.</me-text>
 *   </sn-comparison>
 */
@Component({
  tag: 'sn-comparison',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcComparisonTable {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Table column header labels.
   * Each item can be a plain string or an object with a `label` property.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} label - Column header text
   * @item.required label
   * @item.content label
   * @minItems 4
   */
  @Prop({ mutable: true }) columns?: (string | { label: string })[] | string;

  /**
   * Comparison rows. `values` entries are boolean (→ check/X) or string (→ text).
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} feature - Row label (first column)
   * @item.property {array} values - Cell values aligned to columns 2..n
   * @item.required feature
   * @item.required values
   * @item.content feature values
   * @minItems 4
   */
  @Prop({ mutable: true }) rows?: { feature?: string; values?: Array<boolean | string> }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `columns`/`rows` props.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} subtitle - Fallback subtitle (use slot="subtitle" instead)
   * @property {array} columns - Fallback column headers (use columns prop instead)
   * @property {array} rows - Fallback row data (use rows prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        subtitle?: string;
        raw_html?: string;
        columns?: string[];
        rows?: { feature?: string; values?: Array<boolean | string> }[];
      }
    | string;

  componentWillLoad() {
    this.values  = parseJsonProp(this.values);
    this.columns = parseJsonProp(this.columns);
    this.rows    = parseJsonProp(this.rows);
  }

  private renderValueCell(value: boolean | string) {
    if (typeof value === 'boolean') {
      return (
        <me-icon
          name={value ? 'interface--check' : 'interface--x'}
          class={`${COMPONENT_NAME}__icon ${value ? `${COMPONENT_NAME}__icon--check` : `${COMPONENT_NAME}__icon--x`}`}
          lazy={false}
        ></me-icon>
      );
    }
    return <span class={`${COMPONENT_NAME}__value-text`}>{value}</span>;
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const rawColumns = Array.isArray(this.columns)
      ? this.columns
      : (Array.isArray(values?.columns) ? values.columns : []);
    const columns = rawColumns.map((col: string | { label: string }) =>
      typeof col === 'string' ? col : col?.label ?? ''
    );
    const rows = Array.isArray(this.rows)
      ? this.rows
      : (Array.isArray(values?.rows) ? values.rows : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__header`}>
          <slot name="heading">
            {values?.title && (
              <me-heading variant="4" tag="h4">
                {values.title}
              </me-heading>
            )}
          </slot>
          <slot name="subtitle">{values?.subtitle && <me-text variant="large">{values.subtitle}</me-text>}</slot>
        </div>
        {(columns.length > 0 || rows.length > 0) && (
          <div class={`${COMPONENT_NAME}__table-wrapper`}>
            <table class={`${COMPONENT_NAME}__table`}>
              {columns.length > 0 && (
                <thead>
                  <tr class={`${COMPONENT_NAME}__row ${COMPONENT_NAME}__row--header`}>
                    {columns.map(col => (
                      <th class={`${COMPONENT_NAME}__cell ${COMPONENT_NAME}__cell--header`}>{col}</th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {rows.map(row => {
                  const vals = Array.isArray(row.values) ? row.values : [];
                  return (
                    <tr class={`${COMPONENT_NAME}__row`}>
                      <td class={`${COMPONENT_NAME}__cell ${COMPONENT_NAME}__cell--feature`}>{row.feature}</td>
                      {vals.map(val => (
                        <td class={`${COMPONENT_NAME}__cell ${COMPONENT_NAME}__cell--value`}>{this.renderValueCell(val)}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </me-section>
    );
  }
}
